import {Game} from "../Game";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {SCATTER_ATTENTION, SCATTER_METER_MAP} from "../server/IData";
import {Symbol, SymbolStates} from "../reels/Symbol";

export class ScatterInventory extends PIXI.Container {

    private _animations: PIXI.spine.Spine[];

    constructor() {
        super();
        this.name = "";

        this.init();
        this.playInventoryAnimation();
        this.controlScatterAttentionOnWalk();
    }

    private _count: number = Game.model.initData.scatterCount || 0;

    public get count(): number {
        return this._count;
    }

    public onResize(): void {
        const gridName: string = Game.ACTIVE_REELS_NUM + "by" + Game.ACTIVE_ROWS_NUM;
        const [xPos, yPos] = this.getSMeterPositions(gridName);
        this.position.set(xPos, yPos);
    }

    public updateScatter(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._count < 4) {
            this._count++;
        }

        // #151
        if (Game.model.data.mode.indexOf("FREESPIN") !== -1) {
            tl.add(() => {
                Game.keypadPlugin.setZeroBetSpinCounter(Math.max(Game.model.data.freespinsLeft, 0));
            });
        } else {
            tl.add([
                this.playInventoryAnimation(),
                this.controlScatterAttentionOnWalk()
            ]);
        }

        return tl;
    }

    public stopAllScatters(): void {
        this._count = 0;
        this.stopInventoryAnimations();
        this._animations.forEach((animation: PIXI.spine.Spine) => {
            animation.autoUpdate = true;
            animation.state.setAnimation(0, SCATTER_METER_MAP[this._count], false);
        });
    }

    public onGridExpansion(gridName: string, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const [xPos, yPos] = this.getSMeterPositions(gridName);

        tl.to(this, {
            x: xPos,
            y: yPos,
            duration: duration
        }, "<");

        return tl;
    }

    public stopAnticipation(): void {
        this._animations.forEach((animation: PIXI.spine.Spine) => {
            if (animation.state.getCurrent(1)) {
                Game.clearPlayingTrack(animation, 1, animation.state.getCurrent(1).animation.duration - animation.state.getCurrent(1).trackTime);
            }
            if (animation.state.getCurrent(2)) {
                Game.clearPlayingTrack(animation, 2, animation.state.getCurrent(2).animation.duration - animation.state.getCurrent(2).trackTime);
            }
        });
    }

    public showSMeter(duration: number = 0.2): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.to(this, {
            duration: duration,
            alpha: 1
        });
        return tl;
    }

    public hideSMeter(duration: number = 0.2): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.stopInventoryAnimations();
        });
        tl.to(this, {
            duration: duration,
            alpha: 0
        });
        return tl;
    }

    public getStateAnimation(state: string, loop: boolean = false, trackIndex: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this._animations.forEach((animation: PIXI.spine.Spine) => {
            if (animation) {
                tl.add(Game.createTimelineForAnimation(animation, trackIndex, state, loop), 0);
            }
        })

        return tl;
    }

    public controlScatterAttentionOnWalk(forceStop: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let reel: number = 0; reel < Game.ACTIVE_REELS_NUM; reel++) {
            for (let row: number = 0; row < Game.ACTIVE_ROWS_NUM; row++) {
                const symbol: Symbol = Game.reelsView.reelModels[reel].getSymbolAt(row);
                if (symbol?.name === "S") {
                    if (forceStop) {
                        tl.add(symbol.getStateAnimation(SymbolStates.NORMAL));
                    } else {
                        tl.add(this.playScatterAttention(symbol));
                    }
                }
            }
        }

        return tl;
    }

    public stopInventoryAnimations(isFeatureIntro: boolean = false): void {
        this._animations.forEach((animation: PIXI.spine.Spine) => {
            if (isFeatureIntro) {
                animation.state.getCurrent(2).loop = false;
            }
            Game.clearPlayingTrack(animation);
        });
    }

    public playScatterAttention(symbol: Symbol): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            count: number = this._count;

        if (Game.model.data.mode.includes("NORMAL")) {
            tl.add(() => {
                if (count === 2) {
                    symbol.getStateAnimationWithoutTL(SCATTER_ATTENTION.S2, 0, true);
                } else if (count === 3) {
                    symbol.getStateAnimationWithoutTL(SCATTER_ATTENTION.S3, 0, true);
                }
            });
        }

        return tl;
    }

    private playInventoryAnimation(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            count: number = this._count,
            animName: string = SCATTER_METER_MAP[count];

        if (!animName){
            return tl;
        }

        if (count === 4) {
            tl.add(() => {
                this._animations.forEach((animation: PIXI.spine.Spine) => {
                    if (animation.state.getCurrent(2)) {
                        Game.clearPlayingTrack(animation, 2, animation.state.getCurrent(2).animation.duration - animation.state.getCurrent(2).trackTime);
                    }
                });
            });
        }
        tl.add(this.getStateAnimation(animName));
        tl.add(() => {
            this._animations.forEach((animation: PIXI.spine.Spine) => {
                animation.autoUpdate = true;
                if ([2, 3].includes(count)) {
                    animation.state.setAnimation(1, SCATTER_METER_MAP[(count + 1) + "A"], true);
                }
            });

            if ([3, 4].includes(count)) {
                this._animations.forEach((animation: PIXI.spine.Spine) => {
                    animation.autoUpdate = true;
                    if (count === 3) {
                        animation.state.setAnimation(2, SCATTER_METER_MAP["CARNAGE_SPINS_LOOP"], true);
                    } else {
                        animation.state.setAnimation(2, SCATTER_METER_MAP["CARNAGE_ROYAL_LOOP"], true);
                    }
                });
            }
        });

        return tl;
    }

    private init(): void {
        this._animations = [];
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("sMeterLandscape")));
        this._animations.push(new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("sMeterPortrait")));

        for (let animation of this._animations) {
            animation.visible = true;
            animation.alpha = 0;
            this.addChild(animation);
        }

        this.onResize();
    }

    private getSMeterPositions(gridName: string): number[] {
        if (!NolimitApplication.isLandscape) { // portrait
            this._animations[0].alpha = 0;
            this._animations[1].alpha = 1;
            switch (gridName) {
                case "8by6":
                    return [160, 40]
                case "6by8":
                    return [240, -45]
                case "8by8":
                    return [160, -45]
                case "9by8":
                    return [160, -10]
                case "8by9":
                    return [190, -45]
                case "9by9":
                    return [155, -40]
                default:
                    return [170, -15]
            }
        } else { // landscape
            this._animations[1].alpha = 0;
            this._animations[0].alpha = 1;
            switch (gridName) {
                case "8by6":
                    return [-70, 310]
                case "6by8":
                    return [20, 210]
                case "8by8":
                    return [-70, 210]
                case "9by8":
                    return [-60, 250]
                case "8by9":
                    return [-30, 220]
                case "9by9":
                    return [-70, 220];
                default:
                    return [-50, 230]
            }
        }
    }
}